import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Gauge, Loader2, Save } from "lucide-react";
import { SPORT_REF_DEFS } from "@/lib/trainingDisciplines";
import { sportRefsFromApi } from "@/components/training/SportRefsPanel";

const ENDURANCE_KEYS = ["ftp_w", "threshold_pace_sec_km", "css_sec_100m", "vma_kmh"];

function displayToRef(key, display) {
  if (!display) return null;
  if (key === "threshold_pace_sec_km") {
    const m = String(display).match(/^(\d+):(\d{1,2})$/);
    if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    return null;
  }
  if (key === "css_sec_100m") {
    const m = String(display).match(/^(\d+):(\d{1,2})$/);
    if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    return null;
  }
  const n = parseFloat(display);
  return Number.isNaN(n) ? null : n;
}

export default function SportRefsEditor({ compact = false, onSaved }) {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sport-refs");
      setValues(sportRefsFromApi(data || []));
    } catch {
      setValues({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const refs = ENDURANCE_KEYS.map((key) => ({
        ref_key: key,
        value: displayToRef(key, values[key]),
      })).filter((r) => r.value != null);
      const { data } = await api.put("/sport-refs", { refs });
      onSaved?.(data || []);
      toast.success("Références endurance enregistrées");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement références...
      </div>
    );
  }

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      <div className="flex items-center gap-2">
        <Gauge className="w-4 h-4 text-cyan-600" />
        <p className="text-sm font-semibold text-slate-800">Mes références endurance</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {ENDURANCE_KEYS.map((key) => {
          const def = SPORT_REF_DEFS[key];
          return (
            <div key={key}>
              <Label className="text-xs text-slate-500">{def.label}</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={values[key] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  placeholder={def.placeholder}
                  className="h-9"
                />
                <span className="text-xs text-slate-400 w-12 shrink-0">{def.unit}</span>
              </div>
            </div>
          );
        })}
      </div>
      <Button type="button" size="sm" onClick={save} disabled={saving} className="gap-1.5 rounded-full">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        Enregistrer
      </Button>
    </div>
  );
}